/**
 * Конкурсы платформы.
 *
 * Конкурс создаёт и ведёт платформа, а не бренд: победителя здесь выбирают
 * люди, и отвечать за процедуру должен тот, у кого нет своего интереса в
 * исходе. Бренд можно указать — тогда конкурс идёт «вместе с ним», но этапами
 * и модерацией управляет платформа.
 *
 * Этапы двигаются только вперёд и по одному: приём → голосование → итоги.
 * Вернуть голосование в приём нельзя — работа, пришедшая после того, как люди
 * начали голосовать, соревнуется в других условиях, чем все остальные.
 */
import type { Contest, ContestEntry, ContestStatus } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Icon, Skeleton, fmtShortDay, num, plural } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { LangTabs, setTr, tr, type ContentLang } from '../components/LangTabs';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

/** Следующий этап и подпись кнопки. Дальше итогов пути нет. */
const NEXT: Partial<Record<ContestStatus, { to: ContestStatus; label: string; warn: string }>> = {
  draft: {
    to: 'open',
    label: 'Открыть приём',
    warn: 'Конкурс станет виден покупателям, и в меню приложения появится раздел «Конкурсы». Условия после этого не меняются.',
  },
  open: {
    to: 'voting',
    label: 'Начать голосование',
    warn: 'Приём работ закроется. Работы, не прошедшие проверку, в голосование не попадут.',
  },
  voting: {
    to: 'finished',
    label: 'Подвести итоги',
    warn: 'Голосование закроется, итоги зафиксируются снимком, и счётчики откроются всем. Отменить нельзя.',
  },
};

const EMPTY: Partial<Contest> = {
  title: '',
  tagline: '',
  about: '',
  kind: 'photo',
  vote_mode: 'public',
  votes_per_user: 3,
  jury_weight: 5,
  show_tally: false,
  winners: 3,
  prize: '',
  regions: [],
};

export function ContestsScreen() {
  const data = useResource(() => api.adminContests(), []);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<Contest> | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const stage = async (c: Contest) => {
    const next = NEXT[c.status];
    if (!next) return;
    if (!window.confirm(`${next.label}: ${c.title}.\n\n${next.warn}`)) return;
    try {
      await api.adminContestStage(c.id, next.to);
      toast(`Этап: ${next.to === 'open' ? 'приём работ' : next.to === 'voting' ? 'голосование' : 'итоги'}`);
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  const contests = data.data?.contests ?? [];
  const statuses = data.data?.statuses ?? {};
  const kinds = data.data?.kinds ?? {};

  return (
    <Layout
      title="Конкурсы"
      subtitle="здесь выигрывает работа, а не билет: этапы, работы и голосование ведёт платформа"
      action={
        canModerate ? (
          <Button variant="pri" icon="plus" onClick={() => setEditing({ ...EMPTY })}>
            Новый конкурс
          </Button>
        ) : undefined
      }
    >
      <ReadOnlyNote />

      {data.loading && <Skeleton count={3} height={72} />}
      {data.data && contests.length === 0 && (
        <Empty icon="star" title="Конкурсов нет">
          Пока конкурс не создан, раздела «Конкурсы» в приложении покупателя нет вовсе — пустой
          раздел в меню обещает то, чего нет.
        </Empty>
      )}

      {contests.length > 0 && (
        <Card>
          {contests.map((c) => {
            const next = NEXT[c.status];
            return (
              <div key={c.id} className="krow-adm">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{c.title}</strong>
                    <Badge tone={c.status === 'draft' ? undefined : c.status === 'finished' ? 'good' : 'accent'}>
                      {statuses[c.status] ?? c.status}
                    </Badge>
                    <Badge>{kinds[c.kind] ?? c.kind}</Badge>
                    {c.show_tally && <Badge tone="warn">счётчики открыты</Badge>}
                  </div>
                  {c.tagline && <div className="n">{c.tagline}</div>}
                  <div className="meta">
                    <span>
                      {num(c.entries)} {plural(c.entries, 'работа', 'работы', 'работ')}
                    </span>
                    <span>
                      {num(c.votes)} {plural(c.votes, 'голос', 'голоса', 'голосов')}
                    </span>
                    <span>
                      {c.winners} {plural(c.winners, 'место', 'места', 'мест')}
                    </span>
                    <span>{c.votes_per_user} голосов на человека</span>
                    {c.submit_to && <span>приём до {fmtShortDay(c.submit_to)}</span>}
                    {c.vote_to && <span>голоса до {fmtShortDay(c.vote_to)}</span>}
                    {c.regions && c.regions.length > 0 && <span>{c.regions.join(' · ')}</span>}
                    {c.result_hash && <span className="mono">{c.result_hash.slice(0, 12)}…</span>}
                  </div>
                </div>

                <div className="row" style={{ gap: 6 }}>
                  <Button size="sm" onClick={() => setOpen(open === c.id ? null : c.id)}>
                    Работы
                  </Button>
                  {canModerate && (
                    <>
                      <Button size="sm" onClick={() => setEditing(c)}>
                        Править
                      </Button>
                      {next && (
                        <Button size="sm" variant="pri" onClick={() => void stage(c)}>
                          {next.label}
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {open === c.id && <Entries contest={c} onChange={data.reload} />}
              </div>
            );
          })}
        </Card>
      )}

      {editing && (
        <ContestDialog
          contest={editing}
          kinds={kinds}
          voteModes={data.data?.vote_modes ?? {}}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

/**
 * Работы одного конкурса.
 *
 * Сначала — те, что ждут проверки: это очередь, а не архив. Решение
 * подписывается тем, кто его принял, — отказ без автора нельзя оспорить.
 */
function Entries({ contest, onChange }: { contest: Contest; onChange: () => void }) {
  const { canModerate } = useAdmin();
  const toast = useToast();
  const list = useResource(() => api.adminContestEntries(contest.id), [contest.id]);

  const decide = async (e: ContestEntry, approve: boolean) => {
    let note = '';
    if (!approve) {
      const answer = window.prompt('Почему работа не подходит? Причину увидит участник.', '');
      if (answer === null) return;
      note = answer;
    }
    try {
      await api.adminModerateEntry(e.id, approve, note);
      toast(approve ? 'Работа опубликована' : 'Работа отклонена');
      list.reload();
      onChange();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  const entries = list.data?.entries ?? [];
  const order = { pending: 0, approved: 1, rejected: 2 } as const;
  const sorted = [...entries].sort((a, b) => order[a.status] - order[b.status]);

  return (
    <div className="kadm-entries">
      {list.loading && <Skeleton count={2} height={48} />}
      {list.data && entries.length === 0 && (
        <div className="note">
          <Icon name="eye" size={14} />
          <span>Работ пока нет.</span>
        </div>
      )}
      {sorted.map((e) => (
        <div key={e.id} className={`kadm-entry ${e.status}`}>
          {e.media_url && (
            <a href={e.media_url} target="_blank" rel="noreferrer" className="kadm-thumb">
              {/\.(mp4|webm)$/i.test(e.media_url) ? (
                <Icon name="external" size={16} />
              ) : (
                <img src={e.media_url} alt="" loading="lazy" />
              )}
            </a>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="row" style={{ gap: 8 }}>
              <strong style={{ fontSize: 13 }}>{e.title || 'Без названия'}</strong>
              <Badge
                tone={e.status === 'approved' ? 'good' : e.status === 'rejected' ? 'bad' : 'warn'}
              >
                {e.status === 'approved' ? 'опубликована' : e.status === 'rejected' ? 'отклонена' : 'на проверке'}
              </Badge>
              {contest.status !== 'open' && (
                <span className="faint mono" style={{ fontSize: 11.5 }}>
                  {num(e.votes)}
                </span>
              )}
            </div>
            {e.body && <div className="n">{e.body}</div>}
            <div className="meta">
              <span>{e.team || e.author || 'участник'}</span>
              <span>{fmtShortDay(e.created_at)}</span>
              {e.reject_note && <span>причина: {e.reject_note}</span>}
              {e.moderated_by && <span>решил: {e.moderated_by}</span>}
            </div>
          </div>
          {canModerate && (
            <div className="row" style={{ gap: 6 }}>
              {e.status !== 'approved' && (
                <Button size="sm" variant="pri" onClick={() => void decide(e, true)}>
                  Принять
                </Button>
              )}
              {e.status !== 'rejected' && (
                <Button size="sm" variant="danger" onClick={() => void decide(e, false)}>
                  Отклонить
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ContestDialog({
  contest,
  kinds,
  voteModes,
  onClose,
  onDone,
}: {
  contest: Partial<Contest>;
  kinds: Record<string, string>;
  voteModes: Record<string, string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAdmin();
  const toast = useToast();
  const brands = useResource(() => api.adminBrands(), []);
  const [draft, setDraft] = useState<Partial<Contest>>(contest);
  const [lang, setLang] = useState<ContentLang>('ru');
  const [busy, setBusy] = useState(false);
  // После открытия приёма условия не меняются: они объявлены участникам.
  const locked = !!draft.id && draft.status !== 'draft';

  const set = (patch: Partial<Contest>) => setDraft((d) => ({ ...d, ...patch }));

  const text = (name: 'title' | 'tagline' | 'about' | 'prize') => ({
    value: tr(draft.i18n, lang, name, (draft[name] as string) ?? ''),
    onChange: (e: { target: { value: string } }) =>
      lang === 'ru'
        ? set({ [name]: e.target.value } as Partial<Contest>)
        : set({ i18n: setTr(draft.i18n, lang, name, e.target.value) }),
  });

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminSaveContest(draft);
      toast('Конкурс сохранён');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const toggleRegion = (code: string) => {
    const current = draft.regions ?? [];
    set({
      regions: current.includes(code) ? current.filter((r) => r !== code) : [...current, code],
    });
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 2 }}>
          <span className="h2">{draft.id ? 'Правка конкурса' : 'Новый конкурс'}</span>
          <span className="spacer" />
          <LangTabs lang={lang} onChange={setLang} i18n={draft.i18n} />
        </div>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Условия объявляются до приёма работ и дальше не меняются: названия, количество голосов и
          мест видны участникам с первого дня.
        </p>

        {locked && (
          <div className="note" style={{ marginBottom: 12 }}>
            <Icon name="shield" size={14} />
            <span>
              Конкурс уже открыт — правятся только тексты и обложка. Условия голосования
              заморожены.
            </span>
          </div>
        )}

        <label className="field">
          <span className="lab">Название</span>
          <input className="input" placeholder="Лучшее фото с полки" {...text('title')} />
        </label>

        <label className="field">
          <span className="lab">Одной строкой</span>
          <input className="input" placeholder="Покажи, как выглядит твой город" {...text('tagline')} />
        </label>

        <label className="field">
          <span className="lab">Условия и подробности</span>
          <textarea className="input" rows={4} {...text('about')} />
        </label>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Что присылают</span>
            <select
              className="select"
              disabled={locked}
              value={draft.kind ?? 'photo'}
              onChange={(e) => set({ kind: e.target.value as Contest['kind'] })}
            >
              {Object.entries(kinds).map(([k, name]) => (
                <option key={k} value={k}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="lab">Кто голосует</span>
            <select
              className="select"
              disabled={locked}
              value={draft.vote_mode ?? 'public'}
              onChange={(e) => set({ vote_mode: e.target.value as Contest['vote_mode'] })}
            >
              {Object.entries(voteModes).map(([k, name]) => (
                <option key={k} value={k}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="lab">Голосов на человека</span>
            <input
              className="input mono"
              type="number"
              min={1}
              disabled={locked}
              value={draft.votes_per_user ?? 3}
              onChange={(e) => set({ votes_per_user: Number(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="lab">Вес голоса жюри</span>
            <input
              className="input mono"
              type="number"
              min={1}
              disabled={locked || draft.vote_mode === 'public'}
              value={draft.jury_weight ?? 5}
              onChange={(e) => set({ jury_weight: Number(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="lab">Мест</span>
            <input
              className="input mono"
              type="number"
              min={1}
              disabled={locked}
              value={draft.winners ?? 3}
              onChange={(e) => set({ winners: Number(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="lab">Счётчики голосов</span>
            <select
              className="select"
              disabled={locked}
              value={draft.show_tally ? '1' : '0'}
              onChange={(e) => set({ show_tally: e.target.value === '1' })}
            >
              <option value="0">скрыты до итогов</option>
              <option value="1">видны сразу</option>
            </select>
          </label>

          <label className="field">
            <span className="lab">Приём работ с</span>
            <input
              className="input mono"
              type="date"
              disabled={locked}
              value={dateValue(draft.submit_from)}
              onChange={(e) => set({ submit_from: dateISO(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="lab">Приём работ до</span>
            <input
              className="input mono"
              type="date"
              disabled={locked}
              value={dateValue(draft.submit_to)}
              onChange={(e) => set({ submit_to: dateISO(e.target.value, true) })}
            />
          </label>

          <label className="field">
            <span className="lab">Голосование до</span>
            <input
              className="input mono"
              type="date"
              disabled={locked}
              value={dateValue(draft.vote_to)}
              onChange={(e) => set({ vote_to: dateISO(e.target.value, true) })}
            />
          </label>

          <label className="field">
            <span className="lab">Бренд — необязательно</span>
            <select
              className="select"
              disabled={locked}
              value={draft.brand_id ?? ''}
              onChange={(e) => set({ brand_id: e.target.value })}
            >
              <option value="">без бренда, от платформы</option>
              {(brands.data?.brands ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="lab">Приз</span>
          <input className="input" placeholder="Например: 3 000 сомони и витрина бренда" {...text('prize')} />
        </label>

        <label className="field">
          <span className="lab">Обложка — адрес картинки</span>
          <input
            className="input mono"
            value={draft.cover_url ?? ''}
            placeholder="https://…"
            onChange={(e) => set({ cover_url: e.target.value })}
          />
        </label>

        <div className="field">
          <span className="lab">Страны — пусто значит «все»</span>
          <div className="chips" style={{ marginTop: 6 }}>
            {Object.entries(me?.regions ?? {}).map(([code, name]) => (
              <button
                key={code}
                type="button"
                className={`chip ${(draft.regions ?? []).includes(code) ? 'on' : ''}`}
                disabled={locked}
                onClick={() => toggleRegion(code)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button
            variant="pri"
            disabled={busy || (draft.title ?? '').trim().length < 3}
            onClick={submit}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}

function dateValue(iso?: string): string {
  return iso ? iso.slice(0, 10) : '';
}

function dateISO(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`).toISOString();
}
